import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { 
  Calendar, CheckCircle, XCircle, UserCheck, Clock, Users,
  BookOpen, FileText, Loader2, ChevronLeft, ChevronRight,
  Download, TrendingUp, AlertTriangle
} from 'lucide-react';

const ClassDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'month'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Only fetch current month's classes by default
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const dateFrom = `${year}-${month}-01`;
      const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
      const dateTo = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
      
      const [classesRes, teachersRes] = await Promise.all([
        api.getClasses({ date_from: dateFrom, date_to: dateTo }),
        api.getTeachers()
      ]);
      setClasses(classesRes.data);
      setTeachers(teachersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Filter classes by selected date/range
  const getFilteredClasses = () => {
    return classes.filter(cls => {
      const classDate = new Date(cls.scheduled_date).toISOString().split('T')[0];
      
      if (viewMode === 'day') {
        return classDate === selectedDate;
      } else if (viewMode === 'week') {
        const start = new Date(selectedDate);
        start.setDate(start.getDate() - start.getDay()); // Start of week (Sunday)
        const end = new Date(start);
        end.setDate(end.getDate() + 6); // End of week (Saturday)
        const clsDate = new Date(classDate);
        return clsDate >= start && clsDate <= end;
      } else if (viewMode === 'month') {
        const [year, month] = selectedDate.split('-');
        const [clsYear, clsMonth] = classDate.split('-');
        return year === clsYear && month === clsMonth;
      }
      return true;
    });
  };

  const filteredClasses = getFilteredClasses();

  // Helper to normalize status for comparison
  const normalizeStatus = (status) => status?.toLowerCase() || '';
  const isStatus = (cls, ...statuses) => statuses.some(s => normalizeStatus(cls.status) === s.toLowerCase());

  // Calculate stats for filtered classes (case-insensitive)
  const stats = {
    total: filteredClasses.length,
    scheduled: filteredClasses.filter(c => isStatus(c, 'Scheduled')).length,
    runAsPerPlan: filteredClasses.filter(c => isStatus(c, 'Run as per Plan', 'run_as_per_plan', 'completed')).length,
    cancelled: filteredClasses.filter(c => isStatus(c, 'Cancelled')).length,
    proxyUsed: filteredClasses.filter(c => isStatus(c, 'Proxy Teacher Used', 'proxy_teacher_used', 'substitute_arranged')).length,
    withPdf: filteredClasses.filter(c => c.pdf_attached).length,
    withDpp: filteredClasses.filter(c => c.dpp_attached).length,
  };

  // Get unique teachers who conducted classes
  const activeTeachers = [...new Set(filteredClasses.map(c => c.teacher_id))].length;

  // Get classes by teacher
  const classesByTeacher = filteredClasses.reduce((acc, cls) => {
    const teacher = teachers.find(t => t.id === cls.teacher_id);
    const teacherName = teacher?.name || 'Unknown';
    if (!acc[teacherName]) acc[teacherName] = { total: 0, completed: 0, cancelled: 0 };
    acc[teacherName].total++;
    if (isStatus(cls, 'Run as per Plan', 'run_as_per_plan', 'completed')) acc[teacherName].completed++;
    if (isStatus(cls, 'Cancelled')) acc[teacherName].cancelled++;
    return acc;
  }, {});

  // Navigation functions
  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    if (viewMode === 'day') {
      date.setDate(date.getDate() + direction);
    } else if (viewMode === 'week') {
      date.setDate(date.getDate() + (direction * 7));
    } else if (viewMode === 'month') {
      date.setMonth(date.getMonth() + direction);
    }
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const getDateRangeLabel = () => {
    const date = new Date(selectedDate);
    if (viewMode === 'day') {
      return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    } else if (viewMode === 'week') {
      const start = new Date(date);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Time', 'Title', 'Teacher', 'Subject', 'Status', 'Duration', 'PDF', 'DPP'];
    const rows = filteredClasses.map(cls => {
      const teacher = teachers.find(t => t.id === cls.teacher_id);
      return [
        new Date(cls.scheduled_date).toLocaleDateString(),
        new Date(cls.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        cls.title,
        teacher?.name || 'Unknown',
        cls.subject,
        cls.status,
        cls.duration_minutes,
        cls.pdf_attached ? 'Yes' : 'No',
        cls.dpp_attached ? 'Yes' : 'No'
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `class_summary_${selectedDate}.csv`;
    link.click();
    toast.success('Exported successfully');
  };

  // Navigation handler for clickable stat cards
  const navigateToFilteredClasses = (filterType, filterValue) => {
    const params = new URLSearchParams();
    params.set('date', selectedDate);
    
    if (filterType === 'status') {
      params.set('status', filterValue);
    } else if (filterType === 'material') {
      params.set('material', filterValue);
    }
    
    navigate(`/classes/list?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div data-testid="class-dashboard" className="space-y-6">
      {/* Header with Date Navigation */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Class Summary</h1>
            <p className="text-slate-500 text-sm">View and analyze class statistics by date</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex bg-slate-100 rounded-lg p-1">
              {['day', 'week', 'month'].map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    viewMode === mode 
                      ? 'bg-white shadow text-indigo-600' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>

            {/* Date Picker */}
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            />

            {/* Export Button */}
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Download size={16} />
              Export
            </button>
          </div>
        </div>

        {/* Date Navigation */}
        <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-slate-100">
          <button
            onClick={() => navigateDate(-1)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="text-lg font-semibold text-slate-800 min-w-[300px] text-center">
            {getDateRangeLabel()}
          </span>
          <button
            onClick={() => navigateDate(1)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ChevronRight size={20} />
          </button>
          <button
            onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
            className="px-3 py-1 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            Today
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <StatCard 
          title="Total Classes" 
          value={stats.total} 
          icon={BookOpen} 
          color="bg-indigo-500" 
          bgColor="bg-indigo-50"
          onClick={() => navigateToFilteredClasses('status', 'all')}
          clickable
        />
        <StatCard 
          title="Scheduled" 
          value={stats.scheduled} 
          icon={Calendar} 
          color="bg-blue-500" 
          bgColor="bg-blue-50"
          onClick={() => navigateToFilteredClasses('status', 'Scheduled')}
          clickable
        />
        <StatCard 
          title="Run as per Plan" 
          value={stats.runAsPerPlan} 
          icon={CheckCircle} 
          color="bg-emerald-500" 
          bgColor="bg-emerald-50"
          onClick={() => navigateToFilteredClasses('status', 'Run as per Plan')}
          clickable
        />
        <StatCard 
          title="Cancelled" 
          value={stats.cancelled} 
          icon={XCircle} 
          color="bg-red-500" 
          bgColor="bg-red-50"
          onClick={() => navigateToFilteredClasses('status', 'Cancelled')}
          clickable
        />
        <StatCard 
          title="Proxy Teacher" 
          value={stats.proxyUsed} 
          icon={UserCheck} 
          color="bg-amber-500" 
          bgColor="bg-amber-50"
          onClick={() => navigateToFilteredClasses('status', 'Proxy Teacher Used')}
          clickable
        />
        <StatCard 
          title="PDF Attached" 
          value={stats.withPdf} 
          icon={FileText} 
          color="bg-cyan-500" 
          bgColor="bg-cyan-50"
          onClick={() => navigateToFilteredClasses('material', 'pdf')}
          clickable
        />
        <StatCard 
          title="DPP Attached" 
          value={stats.withDpp} 
          icon={FileText} 
          color="bg-purple-500" 
          bgColor="bg-purple-50"
          onClick={() => navigateToFilteredClasses('material', 'dpp')}
          clickable
        />
      </div>

      {/* Summary Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Completion Rate */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-600 mb-4">Completion Rate</h3>
          <div className="flex items-center justify-center">
            <div className="relative w-32 h-32">
              <svg className="w-32 h-32 transform -rotate-90">
                <circle cx="64" cy="64" r="56" strokeWidth="12" stroke="#e2e8f0" fill="none" />
                <circle
                  cx="64" cy="64" r="56"
                  strokeWidth="12"
                  stroke="#10b981"
                  fill="none"
                  strokeDasharray={`${stats.total > 0 ? (stats.runAsPerPlan / stats.total) * 352 : 0} 352`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-slate-900">
                  {stats.total > 0 ? Math.round((stats.runAsPerPlan / stats.total) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-slate-500 mt-2">
            {stats.runAsPerPlan} of {stats.total} classes completed
          </p>
        </div>

        {/* Materials Coverage */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-600 mb-4">Materials Coverage</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">PDF Attached</span>
                <span className="font-medium">{stats.withPdf}/{stats.total}</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-cyan-500 rounded-full transition-all"
                  style={{ width: `${stats.total > 0 ? (stats.withPdf / stats.total) * 100 : 0}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">DPP Attached</span>
                <span className="font-medium">{stats.withDpp}/{stats.total}</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-purple-500 rounded-full transition-all"
                  style={{ width: `${stats.total > 0 ? (stats.withDpp / stats.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
          {stats.total > 0 && (stats.withPdf < stats.total || stats.withDpp < stats.total) && (
            <div className="mt-4 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-sm text-amber-700">
              <AlertTriangle size={16} />
              <span>{stats.total - stats.withPdf} classes missing PDF</span>
            </div>
          )}
        </div>

        {/* Active Teachers */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-600 mb-4">Active Teachers</h3>
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
                <Users size={28} className="text-indigo-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{activeTeachers}</p>
                <p className="text-sm text-slate-500">Teachers</p>
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-slate-500">
            Conducted classes in this period
          </p>
        </div>
      </div>

      {/* Teacher-wise Breakdown */}
      {Object.keys(classesByTeacher).length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Teacher-wise Summary</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Teacher</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Total</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Completed</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Cancelled</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Success Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {Object.entries(classesByTeacher)
                  .sort((a, b) => b[1].total - a[1].total)
                  .map(([teacher, data]) => (
                    <tr key={teacher} className="hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium text-slate-900">{teacher}</td>
                      <td className="py-3 px-4 text-center">{data.total}</td>
                      <td className="py-3 px-4 text-center text-emerald-600 font-medium">{data.completed}</td>
                      <td className="py-3 px-4 text-center text-red-600 font-medium">{data.cancelled}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          data.total > 0 && (data.completed / data.total) >= 0.8
                            ? 'bg-emerald-100 text-emerald-700'
                            : data.total > 0 && (data.completed / data.total) >= 0.5
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0}%
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Classes List */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Classes ({filteredClasses.length})</h3>
        {filteredClasses.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Calendar size={48} className="mx-auto mb-4 text-slate-300" />
            <p>No classes found for this period</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {filteredClasses
              .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))
              .map(cls => {
                const teacher = teachers.find(t => t.id === cls.teacher_id);
                const statusLower = cls.status?.toLowerCase() || '';
                return (
                  <div 
                    key={cls.id} 
                    className={`p-4 rounded-lg border ${
                      statusLower === 'run as per plan' || statusLower === 'run_as_per_plan' || statusLower === 'completed' ? 'bg-emerald-50 border-emerald-200' :
                      statusLower === 'cancelled' ? 'bg-red-50 border-red-200' :
                      statusLower === 'proxy teacher used' || statusLower === 'proxy_teacher_used' || statusLower === 'substitute_arranged' ? 'bg-amber-50 border-amber-200' :
                      'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-slate-900">{cls.title}</h4>
                        <p className="text-sm text-slate-600">{teacher?.name || 'Unknown'} • {cls.subject}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {new Date(cls.scheduled_date).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {new Date(cls.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span>{cls.duration_minutes} mins</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {cls.pdf_attached && (
                          <span className="px-2 py-1 bg-cyan-100 text-cyan-700 rounded text-xs">PDF</span>
                        )}
                        {cls.dpp_attached && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">DPP</span>
                        )}
                        <StatusBadge status={cls.status} />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, bgColor, onClick, clickable }) => (
  <div 
    className={`${bgColor} rounded-xl p-4 border border-slate-200 ${
      clickable ? 'cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all duration-200' : ''
    }`}
    onClick={onClick}
    data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center`}>
        <Icon size={20} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-600">{title}</p>
      </div>
    </div>
  </div>
);

// Status Badge Component
const StatusBadge = ({ status }) => {
  const normalizedStatus = status?.toLowerCase() || '';
  const styles = {
    'scheduled': 'bg-blue-100 text-blue-700',
    'run as per plan': 'bg-emerald-100 text-emerald-700',
    'run_as_per_plan': 'bg-emerald-100 text-emerald-700',
    'completed': 'bg-emerald-100 text-emerald-700',
    'cancelled': 'bg-red-100 text-red-700',
    'proxy teacher used': 'bg-amber-100 text-amber-700',
    'proxy_teacher_used': 'bg-amber-100 text-amber-700',
    'substitute_arranged': 'bg-amber-100 text-amber-700'
  };
  const labels = {
    'scheduled': 'Scheduled',
    'run as per plan': 'Run as per Plan',
    'run_as_per_plan': 'Run as per Plan',
    'completed': 'Run as per Plan',
    'cancelled': 'Cancelled',
    'proxy teacher used': 'Proxy Teacher Used',
    'proxy_teacher_used': 'Proxy Teacher Used',
    'substitute_arranged': 'Proxy Teacher Used'
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[normalizedStatus] || 'bg-slate-100 text-slate-700'}`}>
      {labels[normalizedStatus] || status}
    </span>
  );
};

export default ClassDashboard;
